/**
 * Generated by scripts/generate-drizzle-schema.mjs. Do not edit by hand.
 * This product's inputs: scripts/drizzle-schema.config.mjs.
 * Regenerate: bash scripts/client-schema-fresh.sh (data-model-change skill).
 */

import { DrizzleAppSchema, type PowerSyncSQLiteDatabase } from "@powersync/drizzle-driver";
import { index, integer, real, sqliteTable, text } from "drizzle-orm/sqlite-core";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";

export const activity_recent = sqliteTable(
  "activity_recent",
  {
    id: text("id").primaryKey(),
    scope_id: text("scope_id"),
    organization_id: text("organization_id"),
    entity_type: text("entity_type"),
    entity_id: text("entity_id"),
    action: text("action"),
    actor_user_id: text("actor_user_id"),
    actor_name: text("actor_name"),
    actor_device_id: text("actor_device_id"),
    platform: text("platform"),
    server_ts: text("server_ts"),
    client_ts: text("client_ts"),
    patch: text("patch"),
    batch_id: text("batch_id"),
    reverts_activity_id: text("reverts_activity_id"),
    chain_scope: text("chain_scope"),
    chain_seq: integer("chain_seq"),
    prev_hash: text("prev_hash"),
    row_hash: text("row_hash"),
  },
  (table) => [
    /** ORDER BY server_ts DESC in the activity feed */
    index("activity_recent_by_feed").on(table.server_ts),
    /** WHERE entity_type = ? AND entity_id = ? in the entity history */
    index("activity_recent_by_entity").on(table.entity_type, table.entity_id, table.server_ts),
  ],
);

export const attachment = sqliteTable(
  "attachment",
  {
    id: text("id").primaryKey(),
    filename: text("filename"),
    content_type: text("content_type"),
    size: integer("size"),
    url: text("url"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    user_id: text("user_id"),
    is_shared: integer("is_shared"),
    hash: text("hash"),
    organization_id: text("organization_id"),
  },
  (table) => [
    /** WHERE user_id = ? in getHasLocalDataQuery */
    index("attachment_by_user").on(table.user_id),
  ],
);

export const device = sqliteTable(
  "device",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    organization_id: text("organization_id"),
    name: text("name"),
    hostname: text("hostname"),
    platform: text("platform"),
    agent_version: text("agent_version"),
    enrolled_at: text("enrolled_at"),
    last_seen_at: text("last_seen_at"),
    archived: integer("archived"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? (personal fleet) */
    index("device_by_user").on(table.user_id),
    /** WHERE organization_id = ? (org fleet) */
    index("device_by_org").on(table.organization_id),
  ],
);

export const device_alert = sqliteTable(
  "device_alert",
  {
    id: text("id").primaryKey(),
    device_id: text("device_id"),
    user_id: text("user_id"),
    organization_id: text("organization_id"),
    rule_id: text("rule_id"),
    severity: text("severity"),
    metric: text("metric"),
    value: real("value"),
    threshold: real("threshold"),
    message: text("message"),
    raised_at: text("raised_at"),
    resolved_at: text("resolved_at"),
  },
  (table) => [
    /** WHERE device_id = ? ORDER BY raised_at (device alerts) */
    index("device_alert_by_device").on(table.device_id, table.raised_at),
  ],
);

export const device_part = sqliteTable(
  "device_part",
  {
    id: text("id").primaryKey(),
    device_id: text("device_id"),
    user_id: text("user_id"),
    organization_id: text("organization_id"),
    version: integer("version"),
    seal: text("seal"),
    inventory: text("inventory"),
    collected_at: text("collected_at"),
    created_at: text("created_at"),
  },
  (table) => [
    /** WHERE device_id = ? ORDER BY version DESC (most recent part) */
    index("device_part_by_device").on(table.device_id, table.version),
  ],
);

export const font_family = sqliteTable("font_family", {
  id: text("id").primaryKey(),
  family: text("family"),
  name: text("name"),
  category: text("category"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
});

export const font_variant = sqliteTable("font_variant", {
  id: text("id").primaryKey(),
  font_family_id: text("font_family_id"),
  weight: text("weight"),
  url: text("url"),
  created_at: text("created_at"),
  attachment_id: text("attachment_id"),
});

// Small per-user dataset — indexes not needed for now.
export const member = sqliteTable("member", {
  id: text("id").primaryKey(),
  organization_id: text("organization_id"),
  user_id: text("user_id"),
  role: text("role"),
  created_at: text("created_at"),
});

// Small dataset — indexes not needed for now.
export const organization = sqliteTable("organization", {
  id: text("id").primaryKey(),
  name: text("name"),
  slug: text("slug"),
  logo: text("logo"),
  metadata: text("metadata"),
  created_at: text("created_at"),
});

export const remote_control_session = sqliteTable(
  "remote_control_session",
  {
    id: text("id").primaryKey(),
    device_id: text("device_id"),
    controller_id: text("controller_id"),
    user_id: text("user_id"),
    organization_id: text("organization_id"),
    scope: text("scope"),
    consent_state: text("consent_state"),
    reason: text("reason"),
    started_at: text("started_at"),
    ended_at: text("ended_at"),
  },
  (table) => [
    /** WHERE device_id = ? ORDER BY started_at DESC (device sessions) */
    index("remote_control_session_by_device").on(table.device_id, table.started_at),
  ],
);

// Active user sessions — small dataset, no indexes.
export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
});

// Small global dataset (~20 themes) — indexes not needed.
export const themes = sqliteTable("themes", {
  id: text("id").primaryKey(),
  name: text("name"),
  background: text("background"),
  surface: text("surface"),
  accent: text("accent"),
  glow_colors: text("glow_colors"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  is_dark: integer("is_dark"),
  foreground: text("foreground"),
  field_background: text("field_background"),
  danger: text("danger"),
  success: text("success"),
  warning: text("warning"),
  muted: text("muted"),
  pattern_type: text("pattern_type"),
  pattern_opacity: integer("pattern_opacity"),
  pair_id: text("pair_id"),
  font_normal: text("font_normal"),
  font_medium: text("font_medium"),
  font_semibold: text("font_semibold"),
  font_bold: text("font_bold"),
  radius: text("radius"),
  surface_foreground: text("surface_foreground"),
  surface_secondary: text("surface_secondary"),
  surface_secondary_foreground: text("surface_secondary_foreground"),
  surface_tertiary: text("surface_tertiary"),
  surface_tertiary_foreground: text("surface_tertiary_foreground"),
  overlay: text("overlay"),
  overlay_foreground: text("overlay_foreground"),
  default: text("default"),
  default_foreground: text("default_foreground"),
  accent_foreground: text("accent_foreground"),
  field_foreground: text("field_foreground"),
  field_placeholder: text("field_placeholder"),
  field_border: text("field_border"),
  danger_foreground: text("danger_foreground"),
  success_foreground: text("success_foreground"),
  warning_foreground: text("warning_foreground"),
  segment: text("segment"),
  segment_foreground: text("segment_foreground"),
  border: text("border"),
  focus: text("focus"),
  link: text("link"),
  surface_shadow: text("surface_shadow"),
  overlay_shadow: text("overlay_shadow"),
  field_shadow: text("field_shadow"),
  muted_foreground: text("muted_foreground"),
  divider: text("divider"),
  glass_background: text("glass_background"),
  glass_border: text("glass_border"),
  glass_highlight: text("glass_highlight"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
});

// Single row per user — indexes not needed.
export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name"),
  email: text("email"),
  email_verified: integer("email_verified"),
  image: text("image"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  locale: text("locale"),
  global_theme_id: text("global_theme_id"),
  is_blur: integer("is_blur"),
  font_family: text("font_family"),
  follow_device_theme: integer("follow_device_theme"),
  role: text("role"),
});

export const drizzleSchema = {
  activity_recent,
  attachment,
  device,
  device_alert,
  device_part,
  font_family,
  font_variant,
  member,
  organization,
  remote_control_session,
  session,
  themes,
  user,
};

export const AppSchema = new DrizzleAppSchema(drizzleSchema);

export type Database = (typeof AppSchema)["types"];

/**
 * Type helper for the Drizzle database instance wrapped with PowerSync.
 */
export type PowerSyncDrizzleDB = PowerSyncSQLiteDatabase<typeof drizzleSchema>;

export const USER_DATA_TABLES = [
  "attachment",
  "device",
  "device_alert",
  "device_part",
  "remote_control_session",
] as const;

export type ActivityRecent = InferSelectModel<typeof activity_recent>;
export type ActivityRecentInsert = InferInsertModel<typeof activity_recent>;
export type ActivityRecentRecord = ActivityRecent;
export type Attachment = InferSelectModel<typeof attachment>;
export type AttachmentInsert = InferInsertModel<typeof attachment>;
export type AttachmentRecord = Attachment;
export type Device = InferSelectModel<typeof device>;
export type DeviceInsert = InferInsertModel<typeof device>;
export type DeviceRecord = Device;
export type DeviceAlert = InferSelectModel<typeof device_alert>;
export type DeviceAlertInsert = InferInsertModel<typeof device_alert>;
export type DeviceAlertRecord = DeviceAlert;
export type DevicePart = InferSelectModel<typeof device_part>;
export type DevicePartInsert = InferInsertModel<typeof device_part>;
export type DevicePartRecord = DevicePart;
export type FontFamily = InferSelectModel<typeof font_family>;
export type FontFamilyInsert = InferInsertModel<typeof font_family>;
export type FontFamilyRecord = FontFamily;
export type FontVariant = InferSelectModel<typeof font_variant>;
export type FontVariantInsert = InferInsertModel<typeof font_variant>;
export type FontVariantRecord = FontVariant;
export type Member = InferSelectModel<typeof member>;
export type MemberInsert = InferInsertModel<typeof member>;
export type MemberRecord = Member;
export type Organization = InferSelectModel<typeof organization>;
export type OrganizationInsert = InferInsertModel<typeof organization>;
export type OrganizationRecord = Organization;
export type RemoteControlSession = InferSelectModel<typeof remote_control_session>;
export type RemoteControlSessionInsert = InferInsertModel<typeof remote_control_session>;
export type RemoteControlSessionRecord = RemoteControlSession;
export type Session = InferSelectModel<typeof session>;
export type SessionInsert = InferInsertModel<typeof session>;
export type SessionRecord = Session;
export type Themes = InferSelectModel<typeof themes>;
export type ThemesInsert = InferInsertModel<typeof themes>;
export type ThemesRecord = Themes;
export type User = InferSelectModel<typeof user>;
export type UserInsert = InferInsertModel<typeof user>;
export type UserRecord = User;
